const PERMISSIONS = {
  All: {
    ALL: '*',
  },
  BUSINESS_PARTNER: '+*',
  Analytics: {
    VIEW: 'analytics_view',
    MARKETING_DATA_VIEW: 'marketing_data_view',
  },
  Billing: {
    VIEW: 'billing_view',
  },
  Permissions: {
    VIEW: 'permissions_view',
    EDIT: 'permissions_edit',
  },
  Customers: {
    VIEW: 'customer_view',
    EDIT: 'customer_edit',
    LIKES_VIEW: 'customer_likes_view',
    COLD_PROSPECTS: 'customers_cold_prospects',
  },
  LeadStates: {
    VIEW: 'leadStates_view',
    CREATE: 'leadStates_create',
    EDIT: 'leadStates_edit',
    DELETE: 'leadStates_delete',
  },
  Leads: {
    VIEW: 'leads_view',
    VIEW_ALL: 'leads_view_all',
    CREATE: 'leads_create',
    EDIT: 'leads_edit',
    ASSIGN: 'leads_assign',
    CHANGE_BRANCH: 'leads_change_branch',
    ARCHIVE: 'leads_archive',
    DELETE: 'leads_delete',
    VIEW_FUNNEL_CHART: 'funnel_chart_view',
    VIEW_PERFORMANCE_CHART: 'analytics_view',
  },
  Tasks: {
    VIEW: 'tasks_view',
    CREATE: 'tasks_create',
    CREATE_FOR_OTHERS: 'tasks_create_all',
    EDIT: 'tasks_edit',
  },
  Branches: {
    // Get all branches occasionally - E.g: Lead creation - Lead assignment. If not, only the scoped ones will be returned.
    VIEW_ALL: 'branches_view_all',
    // Expanding the scope. This user will be a member of all the branches for all views. No actual frontend impact on the visibility settings.
    MANAGE_ALL: 'branches_manage_all',
    CREATE: 'branches_create',
    EDIT: 'branches_edit',
    DELETE: 'branches_delete',
  },
  Roles: {
    VIEW: 'roles_view',
    CREATE: 'roles_create',
    EDIT: 'roles_edit',
    DELETE: 'roles_delete',
  },
  ServiceTypes: {
    CREATE: 'serviceTypes_create',
    EDIT: 'serviceTypes_edit',
    DELETE: 'serviceTypes_delete',
  },
  Users: {
    VIEW: 'users_view',
    CREATE: 'users_create',
    DELETE: 'users_delete',
    EDIT: 'users_edit',
    AVAILABILITY_EDIT: 'users_availability_edit',
    PRIORITY_EDIT: 'users_priority_edit',
    DISABLE: 'users_disable',
    ENABLE: 'users_enable',
  },
  BranchServices: {
    VIEW: 'branchService_view',
    CREATE: 'branchService_create',
    EDIT: 'branchService_edit',
    DELETE: 'branchService_delete',
    AGENTS_VIEW: 'branchService_agents_view',
  },
  ServiceRequests: {
    VIEW: 'serviceRequests_view',
    CREATE: 'serviceRequests_create',
    EDIT: 'serviceRequests_edit',
    RESCHEDULE: 'serviceRequests_reschedule',
    STATS: 'serviceRequests_stats',
    CANCEL: 'serviceRequests_cancel',
    CONFIRM: 'serviceRequests_confirm',
    ACTIONS_ALL: 'serviceRequests_actions_all',
    DELETE: 'serviceRequests_delete',
    CONTROL_EMAILS: 'serviceRequests_control_emails',
    MANAGE_ATTENDANCE: 'serviceRequests_manage_attendance',
  },
  CalendarEvents: {
    VIEW: 'calendarEvents_view',
    VIEW_ALL: 'calendarEvents_view_all',
    CREATE: 'calendarEvents_create',
    CREATE_FOR_OTHERS: 'calendarEvents_create_for_others',
  },
  Today: {
    VIEW: 'todays_tasks_view',
  },
  EmailAutomation: {
    VIEW: 'emailAutomations_view',
    EDIT: 'emailAutomations_edit',
    CREATE: 'emailAutomations_create',
    DELETE: 'emailAutomations_delete',
  },
  // region Touchpoints
  // Everything a customer eventually sees. One backend category (`Touchpoints`)
  // and one permission per entity, except email templates: drafting is private
  // and reversible, publishing is what reaches a recipient, so those stay
  // split. Enforced by the Laravel policies of the same names.
  //
  // VIEW is what puts the group in the menu at all — see `TOUCHPOINTS_ROOT` in
  // roleMapping, which is the union of the permissions below.
  EmailTemplate: {
    VIEW: 'email_template_view',
    DRAFT: 'email_template_draft',
    PUBLISH: 'email_template_publish',
    CREATE: 'email_template_create',
    DELETE: 'email_template_delete',
  },
  // Layouts and building blocks: create, edit and delete in one permission.
  EmailFragments: {
    MANAGE: 'components_manage',
  },
  // SMTP connections. Reading is gated too — a mailer row is credentials and
  // the host they belong to (PartnerMailerPolicy::viewAny).
  Mailers: {
    MANAGE: 'mailers_manage',
  },
  PartnerTranslations: {
    EDIT: 'partner_translations_edit',
  },
  // Colours, assets, typography, the default layout, and which languages the
  // partner translates into — replaces the old theme_edit/partner_languages_edit.
  Branding: {
    MANAGE: 'branding_manage',
  },
  // Booking pages and the per-user booking links alike — the same pages, seen
  // from the account rather than from a user (UserPolicy::manageBookingLinks).
  BOOKING_PAGES: {
    MANAGE: 'booking_manage'
  },
  // A journey's client configuration — the Tracking touchpoint (JourneyController).
  Journey: {
    EDIT: 'journey_edit',
  },
  // endregion
  // The partner file library (images, fonts, documents). Enforced by the
  // Laravel FilestorePolicy, which also lets email authors upload without
  // `files_upload` — putting an image in a template is part of writing one.
  Files: {
    VIEW: 'files_view',
    UPLOAD: 'files_upload',
    DELETE: 'files_delete',
  },
  Exports: {
    VIEW: 'exports_view',
  },
  // Sales goals (Laravel partner API). VIEW = own goals + the goals they roll
  // up into; VIEW_ALL = everything in the user's branch/service scope. Writes
  // additionally need manage rights on the target — the API answers with
  // `can_edit` / `can_delete` per goal.
  Goals: {
    VIEW: 'goals_view',
    VIEW_ALL: 'goals_view_all',
    CREATE: 'goals_create',
    EDIT: 'goals_edit',
    DELETE: 'goals_delete',
  },
  SalesInfluencers: {
    VIEW: 'sales_influencers_view',
    MANAGE: 'sales_influencers_manage',
  },
  Skills: {
    VIEW: 'skills_view',
    MANAGE: 'skills_manage',
  },
  LeadSource: {
    VIEW: 'leadSources_view',
    EDIT: 'leadSources_edit',
    CREATE: 'leadSources_create',
    DELETE: 'leadSources_delete',
  },
  Calls: {
    VIEW: 'call_view',
    CALL_CHART_VIEW: 'call_chart_view',
  },
  ServiceType: {
    EDIT: 'serviceTypes_edit',
    CREATE: 'serviceTypes_create',
    DELETE: 'serviceTypes_delete',
  },
  // Plural values since the event-system backend release (taskAutomations_*)
  TaskAutomation: {
    VIEW: 'taskAutomations_view',
    EDIT: 'taskAutomations_edit',
    CREATE: 'taskAutomations_create',
    DELETE: 'taskAutomations_delete',
    MANAGE_ALL: 'taskAutomations_manage_all',
  },
  ExtraFieldType: {
    VIEW: 'extraFieldType_view',
    EDIT: 'extraFieldType_edit',
    CREATE: 'extraFieldType_create',
    DELETE: 'extraFieldType_delete',
  },
  PartnerConfig: {
    EDIT: 'partner_config_edit',
  },
  Offerings: {
    EDIT: 'offerings_edit',
    CREATE: 'offerings_create',
    DELETE: 'offerings_delete',
  },
  PRODUCT_GROUPS: {
    EDIT: 'product_group_edit',
    CREATE: 'product_group_create',
    DELETE: 'product_group_delete',
  },
  API_KEYS: {
    VIEW: 'api_keys_view',
    CREATE: 'api_keys_create',
    EDIT: 'api_keys_edit',
    DELETE: 'api_keys_delete',
  },
  INTEGRATIONS: {
    MANAGE: 'integrations_manage',
    VIEW: 'integrations_view',
  },
  MessageThreads: {
    VIEW: 'message_threads_view',
    VIEW_ALL: 'message_threads_view_all',
    COMPOSE: 'message_threads_compose',
  }
}

export default PERMISSIONS
